import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { UserIcon } from '../components/Icons';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

export const ReaderProfilePage = () => {
  // Get reader ID from URL
  const pathParts = window.location.pathname.split('/');
  const readerId = pathParts[pathParts.length - 1];

  const [reader, setReader] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (readerId) {
      fetchReaderProfile();
    }
  }, [readerId]);

  const fetchReaderProfile = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.readers.byId(readerId), {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch reader profile');
        setLoading(false);
        return;
      }

      setReader(data.reader);
      setLoading(false);
    } catch (err) {
      console.error('Fetch reader profile error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading reader profile...
              </p>
            </div>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !reader) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error || 'Reader not found'}
              </p>
              <Button onClick={() => window.history.back()}>
                Go Back
              </Button>
            </div>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          style={{ marginBottom: '16px' }}
        >
          ← Back
        </Button>

        {/* Reader Header */}
        <Card style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            gap: '24px',
            marginBottom: '24px',
          }}>
            {/* Profile Photo */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: theme.colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: theme.colors.primary,
            }}>
              <UserIcon width={50} height={50} />
            </div>

            {/* Header Info */}
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontSize: '36px',
                fontWeight: '400',
                marginBottom: '8px',
                color: theme.colors.textPrimary,
              }}>
                {reader.displayName || 'Beta Reader'}
              </h1>
              <div style={{
                fontSize: '16px',
                color: theme.colors.textSecondary,
                marginBottom: '16px',
              }}>
                Beta Reader
              </div>
              <Badge variant="primary" size="large">Available to Read</Badge>
            </div>
          </div>

          {/* Reader Bio */}
          {reader.bio && (
            <div style={{
              padding: '20px',
              backgroundColor: theme.colors.backgroundAlt,
              borderRadius: theme.borderRadius.medium,
              marginBottom: '20px',
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                About
              </h3>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.6',
                color: theme.colors.textPrimary,
                whiteSpace: 'pre-wrap',
              }}>
                {reader.bio}
              </p>
            </div>
          )}
        </Card>

        {/* Reading Preferences */}
        <Card style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '20px',
            color: theme.colors.textPrimary,
          }}>
            Reading Preferences
          </h2>

          {/* Preferred Genres */}
          {reader.preferredGenres && reader.preferredGenres.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '12px',
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Preferred Genres
              </h3>
              <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
              }}>
                {reader.preferredGenres.map((genre: string, index: number) => (
                  <Badge key={index} variant="primary">
                    {genre}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Reading Preferences Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
            padding: '20px',
            backgroundColor: theme.colors.backgroundAlt,
            borderRadius: theme.borderRadius.medium,
          }}>
            <div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Experience Level
              </div>
              <div style={{
                fontSize: '16px',
                color: theme.colors.textPrimary,
              }}>
                {reader.experienceLevel === 'BEGINNER' && 'New to beta reading'}
                {reader.experienceLevel === 'INTERMEDIATE' && 'Some beta reading experience'}
                {reader.experienceLevel === 'EXPERIENCED' && 'Experienced beta reader'}
                {!reader.experienceLevel && 'Not specified'}
              </div>
            </div>

            <div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Reading Speed
              </div>
              <div style={{
                fontSize: '16px',
                color: theme.colors.textPrimary,
              }}>
                {reader.readingSpeed === 'SLOW' && '6-8 weeks'}
                {reader.readingSpeed === 'MODERATE' && '3-4 weeks'}
                {reader.readingSpeed === 'FAST' && '2-3 weeks'}
                {!reader.readingSpeed && 'Not specified'}
              </div>
            </div>
          </div>
        </Card>

        {/* Contact/Action Section */}
        <Card>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '20px',
            color: theme.colors.textPrimary,
          }}>
            Connect with this Reader
          </h2>
          <p style={{
            fontSize: '15px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
            marginBottom: '20px',
          }}>
            Interested in having {reader.displayName || 'this reader'} review your manuscript? You can send them a message or invite them to apply for one of your manuscripts.
          </p>
          <div style={{
            display: 'flex',
            gap: '12px',
          }}>
            <Button
              onClick={() => window.location.href = `/messages/new?to=${readerId}`}
            >
              Send Message
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/writer/manuscripts'}
            >
              View My Manuscripts
            </Button>
          </div>
        </Card>
      </main>

      <Footer />
    </div>
  );
};
